
import { useState } from 'react';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useApps, useUserApps } from '@/hooks/useApps';
import { AppCard } from '@/components/apps/AppCard';
import { Search, Package, Settings as SettingsIcon } from 'lucide-react';

const Settings = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { apps, isLoading: appsLoading } = useApps();
  const { userApps, installApp, uninstallApp, toggleApp } = useUserApps();

  const installedAppIds = new Set(userApps.map(ua => ua.app_id));
  
  const filteredApps = apps.filter(app =>
    app.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const availableApps = filteredApps.filter(app => !installedAppIds.has(app.id));
  const myApps = userApps.filter(userApp => 
    userApp.app && (
      userApp.app.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userApp.app.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const getCategoryStats = () => {
    const categories = userApps.reduce((acc, userApp) => {
      if (userApp.app) {
        acc[userApp.app.category] = (acc[userApp.app.category] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(categories).map(([category, count]) => ({
      category: category.replace('-', ' '),
      count
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavbar />
      
      <main className="container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your account settings and installed applications
          </p>
        </div>

        <Tabs defaultValue="apps" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="apps" className="gap-2">
              <Package className="h-4 w-4" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="apps" className="space-y-6">
            {/* App Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Installed Apps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{userApps.length}</div>
                  <p className="text-xs text-muted-foreground">
                    {userApps.filter(ua => ua.is_enabled).length} enabled
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Available Apps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{apps.length}</div>
                  <p className="text-xs text-muted-foreground">
                    {availableApps.length} not installed
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1">
                    {getCategoryStats().map(({ category, count }) => (
                      <Badge key={category} variant="secondary" className="text-xs">
                        {category} ({count})
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search applications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Tabs defaultValue="my-apps" className="space-y-4">
              <TabsList>
                <TabsTrigger value="my-apps">My Apps ({userApps.length})</TabsTrigger>
                <TabsTrigger value="browse">Browse ({availableApps.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="my-apps">
                {myApps.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myApps.map((userApp) => (
                      <AppCard
                        key={userApp.id}
                        app={userApp.app!}
                        userApp={userApp}
                        onInstall={() => {}}
                        onUninstall={(appId) => uninstallApp.mutate(appId)}
                        onToggle={(appId, enabled) => toggleApp.mutate({ appId, enabled })}
                        isUninstalling={uninstallApp.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Package className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No apps installed</h3>
                      <p className="text-muted-foreground text-center">
                        Browse available apps to enhance your platform experience
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="browse">
                {availableApps.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {availableApps.map((app) => (
                      <AppCard
                        key={app.id}
                        app={app}
                        onInstall={(appId) => installApp.mutate(appId)}
                        onUninstall={() => {}}
                        onToggle={() => {}}
                        isInstalling={installApp.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Search className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No apps found</h3>
                      <p className="text-muted-foreground text-center">
                        {searchQuery ? 'Try adjusting your search terms' : 'All available apps are already installed'}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
                <CardDescription>
                  Manage your account preferences and personal information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Account settings coming soon...</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Settings;
